import React, { useContext } from 'react';
import './RecipeTree.css';
import GameAssetsContext from '../../../XXX/context/GameAssetContext';
import { ItemDetails } from '../../../XXX/GameSimulator/GameContainer2/GameGrid/ChampionCard/SettingsPanel/ItemShopPanel2';

// Interface for recipe tree node
interface RecipeTreeNode {
  id: number;
  name: string;
  cost: number;
  components: RecipeTreeNode[];
  /** number of leaf slots this subtree occupies */
  span: number;
}

interface RecipeTreeProps {
  itemId: number;
  apiVersion?: string;
  className?: string;
}

const RecipeTree: React.FC<RecipeTreeProps> = ({ 
  itemId, 
  apiVersion = "15.9.1",
  className = ""
}) => {
  const { gameWideAssets } = useContext(GameAssetsContext);

  // Helper function to get item image URL
  const getItemImageUrl = (itemId: number) => {
    return `https://ddragon.leagueoflegends.com/cdn/${apiVersion}/img/item/${itemId}.png`;
  };

  // Get item details from gameWideAssets
  const getItemDetails = (itemId: number): ItemDetails | null => {
    if (!gameWideAssets?.itemsDetails) return null;
    return gameWideAssets.itemsDetails.find(item => 
      item.id === itemId.toString() || parseInt(item.id) === itemId
    ) || null;
  };

  // Recursive function to build recipe tree and compute spans
  const buildRecipeTree = (
    itemId: number,
    visited: Set<number> = new Set()
  ): RecipeTreeNode | null => {
    if (visited.has(itemId)) return null; // Prevent infinite loops

    const itemDetails = getItemDetails(itemId);
    if (!itemDetails) return null;

    const nextVisited = new Set(visited);
    nextVisited.add(itemId);

    const components: RecipeTreeNode[] = [];
    if (itemDetails.recipe?.buildsFrom) {
      for (const componentIdStr of itemDetails.recipe.buildsFrom) {
        const componentId = parseInt(componentIdStr);
        const child = buildRecipeTree(componentId, nextVisited);
        if (child) components.push(child);
      }
    }

    const span =
      components.length === 0
        ? 1
        : components.reduce((acc, c) => acc + c.span, 0);

    return {
      id: itemId,
      name: itemDetails.name,
      cost: itemDetails.cost,
      components,
      span,
    };
  };

  const recipeTree = buildRecipeTree(itemId);

  if (!recipeTree) {
    return (
      <div className={`recipe-tree-container ${className}`}>
        <div className="no-recipe">No recipe components</div>
      </div>
    );
  }

  return (
    <div className={`recipe-tree-container ${className}`}>
      <RecipeTreeNode node={recipeTree} balanceMode="none" apiVersion={apiVersion} />
    </div>
  );
};

type BalanceMode = 'stretch' | 'offsetRoot' | 'none';

const ITEM_WIDTH = 56;      // sibling width calc
const ITEM_GAP = 16;        // gap between siblings
const BASE_CONNECTOR_H = 30; // minimum vertical space between levels
const EXTRA_PER_LEVEL = 10;  // how much taller we make it per extra depth on any child
const STROKE = '#c9aa71';
const STROKE_W = 2;

interface RecipeTreeNodeProps {
  node: RecipeTreeNode;
  depth?: number;
  balanceMode?: BalanceMode; // 'stretch' | 'offsetRoot' | 'none'
  apiVersion: string;
}

const RecipeTreeNode: React.FC<RecipeTreeNodeProps> = ({
  node,
  depth = 0,
  balanceMode = 'stretch',
  apiVersion,
}) => {
  // Helper function to get item image URL
  const getItemImageUrl = (itemId: number) => {
    return `https://ddragon.leagueoflegends.com/cdn/${apiVersion}/img/item/${itemId}.png`;
  };

  const hasChildren = node.components.length > 0;

  // ---- width math (unchanged) ----
  const subtreeWidth = (n: RecipeTreeNode): number => {
    if (n.components.length === 0) return ITEM_WIDTH;
    const w = n.components.map(subtreeWidth).reduce((a, b) => a + b, 0);
    const gaps = (n.components.length - 1) * ITEM_GAP;
    return Math.max(ITEM_WIDTH, w + gaps);
  };
  const width = subtreeWidth(node);
  const childWidths = node.components.map(subtreeWidth);

  // ---- depth math so we can balance vertically ----
  const depthOf = (n: RecipeTreeNode): number =>
    n.components.length === 0 ? 0 : 1 + Math.max(...n.components.map(depthOf));

  const childDepths = node.components.map(depthOf);
  const maxChildDepth = childDepths.length ? Math.max(...childDepths) : 0;

  // Option 1: stretch the connector area by deeper side
  const connectorH =
    balanceMode === 'stretch'
      ? BASE_CONNECTOR_H + maxChildDepth * EXTRA_PER_LEVEL
      : BASE_CONNECTOR_H;

  // The trunk sits exactly in the middle of the connector area
  const TRUNK_Y = Math.round(connectorH / 2);

  // Option 2: drop the root so the midpoint visually matches the deeper side
  const rootOffsetY =
    balanceMode === 'offsetRoot' ? Math.round((maxChildDepth * EXTRA_PER_LEVEL) / 2) : 0;

  // ---- lay out children row precisely under the parent ----
  const totalChildWidth =
    childWidths.reduce((a, b) => a + b, 0) +
    Math.max(0, node.components.length - 1) * ITEM_GAP;

  const leftPad = (width - totalChildWidth) / 2;

  const centers: number[] = [];
  let cursor = leftPad;
  childWidths.forEach((w, i) => {
    centers.push(cursor + w / 2);
    cursor += w + (i < childWidths.length - 1 ? ITEM_GAP : 0);
  });

  // centers of first and last child (fallback to width/2 if missing)
  const trunkStart = centers.length ? centers[0] : width / 2;
  const trunkEnd   = centers.length ? centers[centers.length - 1] : width / 2;
  
  // delta that centers the trunk under the root (root stays put)
  const trunkMid   = (trunkStart + trunkEnd) / 2;
  const delta      = (width / 2) - trunkMid;
  
  // use shifted coords for drawing and for the children row position
  const sCenters   = centers.map(c => c + delta);
  const sStart     = trunkStart + delta;
  const sEnd       = trunkEnd + delta;

  return (
    <div className={`recipe-tree-node depth-${depth}`} style={{ width }}>
      <div className="recipe-item" style={{ marginTop: rootOffsetY }}>
        <img
          src={getItemImageUrl(node.id)}
          alt={node.name}
          className="recipe-item-image"
          onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
        />
        <div className="recipe-item-info">
          <div className="recipe-item-cost">{node.cost}</div>
        </div>
      </div>

      {hasChildren && (
  <div className="branch" style={{ width }}>
    <svg className="connectors" width={width} height={connectorH} style={{ display: 'block' }} shapeRendering="crispEdges">
      {/* root -> trunk is now fixed at width/2 (root center) */}
      <line x1={width / 2} y1={0} x2={width / 2} y2={TRUNK_Y} stroke={STROKE} strokeWidth={STROKE_W} />
      {/* trunk centered via shifted start/end */}
      {sCenters.length > 1 && (
        <line x1={sStart} y1={TRUNK_Y} x2={sEnd} y2={TRUNK_Y} stroke={STROKE} strokeWidth={STROKE_W} />
      )}
      {/* drops to each child */}
      {sCenters.map((cx, i) => (
        <line key={i} x1={cx} y1={TRUNK_Y} x2={cx} y2={connectorH} stroke={STROKE} strokeWidth={STROKE_W} />
      ))}
    </svg>

    {/* shift the whole children row by the same delta */}
    <div
      className="recipe-components-row"
      style={{
        display: 'flex',
        gap: ITEM_GAP,
        width: totalChildWidth,
        margin: '0 auto',
        transform: `translateX(${delta}px)`,
      }}
    >
      {node.components.map((child, i) => (
        <div key={`${child.id}-${i}`} style={{ width: childWidths[i], display: 'flex', justifyContent: 'center' }}>
          <RecipeTreeNode node={child} depth={depth + 1} balanceMode={balanceMode} apiVersion={apiVersion} />
        </div>
      ))}
    </div>
  </div>
)}
    </div>
  );
};



export default RecipeTree;
